import React, { useState } from "react";
import AuthLayout from "../layouts/auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Alert } from "../components/ui/alert"; // Import Alert component
import { Spinner } from "../components/ui/spinner"; // Import Spinner component
import { cn } from "../lib/utils";
import axios from "axios"; // Assuming you'll use axios for API calls
import { API_BASE_URL } from "../lib/constants";
import { z } from "zod"; // Import zod
import { useTranslation } from "../contexts/I18nProvider";

// Define validation schema using zod
const resetPasswordSchema = z.object({
  email: z
    .string()
    .email("Please enter a valid email address")
    .min(1, "Email is required"),
});

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); // State for handling errors
  const [success, setSuccess] = useState(""); // State for handling success messages
  const [formErrors, setFormErrors] = useState({ email: "" }); // Validation errors state

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(""); // Reset previous errors
    setSuccess(""); // Reset previous success messages
    setFormErrors({ email: "" }); // Clear form errors

    // Validate the form data using zod
    const result = resetPasswordSchema.safeParse({ email });

    if (!result.success) {
      // If validation fails, update form errors state
      const errors = result.error.format();
      setFormErrors({
        email: errors.email ? errors.email._errors[0] : "",
      });
      setLoading(false);
      return; // Stop the submission if validation fails
    }

    // Submit data if validation passes
    try {
      const response = await axios.post(
        API_BASE_URL + "/api/auth/reset-password",
        {
          email,
        }
      );

      if (response.status === 200) {
        setSuccess("A password reset link has been sent to your email.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Failed to send password reset link. Please check your email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card className={cn("min-w-[25rem]")}>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('pages.forgotPassword.title')}</CardTitle>
          <CardDescription>
            {t('pages.forgotPassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="example@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {formErrors.email && (
                  <div className="text-red-500 text-sm mt-2">
                    {formErrors.email}
                  </div>
                )}
              </div>
              <Button
                type="submit"
                className="w-full rounded-xl"
                disabled={loading}
              >
                {loading ? <Spinner /> : t('pages.forgotPassword.sendLink')}
              </Button>
              {error && (
                <Alert variant="destructive" className="mt-4">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" className="mt-4">
                  {success}
                </Alert>
              )}
              <div className="text-center text-sm mt-4">
                {t('pages.forgotPassword.remembered')}{" "}
                <a
                  href="/get-started?tab=login"
                  className="underline underline-offset-4"
                >
                  {t('auth.tabs.login')}
                </a>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

export default ForgotPasswordPage;
